import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import PageHeader from '@/components/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import {
  createSucursalOperativa,
  getApiErrorMessage,
  listLocalidadesOperativas,
  listProvinciasOperativas,
  listSucursalesOperativas,
  toggleSucursalOperativa,
  updateSucursalOperativa,
} from '@/services/sucursalesOperativas.js';

const emptyForm = {
  id: null,
  numero_sucursal: '',
  codigo: '',
  nombre: '',
  razon_social: '',
  cuit: '',
  domicilio_fiscal: '',
  provincia_id: '',
  localidad_id: '',
  codigo_postal: '',
  telefono: '',
  email: '',
  condicion_iva: '',
  es_casa_central: false,
  es_hub_distribucion: false,
  activa: true,
  observaciones: '',
};

const invoiceFields = [
  'razon_social',
  'cuit',
  'domicilio_fiscal',
  'codigo_postal',
  'telefono',
  'email',
  'condicion_iva',
];

function getInvoiceDefaults(sucursales) {
  const source = sucursales.find((sucursal) => sucursal.es_casa_central) || sucursales[0];
  if (!source) return {};

  return invoiceFields.reduce((defaults, field) => {
    if (source[field]) defaults[field] = source[field];
    return defaults;
  }, {});
}

function isNewEmptyForm(form) {
  return !form.id && Object.entries(emptyForm).every(([field, value]) => form[field] === value);
}

const inputClassName = 'h-8.5 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.88)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:bg-[rgba(var(--subpanel),0.8)] disabled:text-[rgba(var(--muted),0.92)]';
const panelClassName = 'rounded-2xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] p-2.5 shadow-[0_10px_30px_rgba(var(--shadow),0.12)] md:p-3';

function BooleanPill({ value, trueLabel = 'Si', falseLabel = 'No' }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${value ? 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--muted),0.14)] text-[rgba(var(--muted),0.95)]'}`}>
      {value ? trueLabel : falseLabel}
    </span>
  );
}

function SectionAlert({ tone = 'danger', children }) {
  const className = tone === 'warning'
    ? 'border-[rgba(var(--brand-700),0.35)] bg-[rgba(var(--brand),0.1)]'
    : 'border-[rgba(var(--danger,220_38_38),0.35)] bg-[rgba(var(--danger,220_38_38),0.08)]';

  return (
    <div className={`rounded-2xl border px-3 py-2 text-sm text-[rgb(var(--fg))] ${className}`}>
      {children}
    </div>
  );
}

export default function SucursalesEmpresaPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loadingRows, setLoadingRows] = useState(true);
  const [loadingProvincias, setLoadingProvincias] = useState(true);
  const [loadingLocalidades, setLoadingLocalidades] = useState(false);
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [rowsError, setRowsError] = useState('');
  const [provinciasError, setProvinciasError] = useState('');
  const [localidadesError, setLocalidadesError] = useState('');
  const [formError, setFormError] = useState('');

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.numero_sucursal, row.nombre, row.provincia_nombre, row.localidad_nombre]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [rows, search]);

  const loadRows = async () => {
    setLoadingRows(true);
    setRowsError('');
    try {
      const data = await listSucursalesOperativas();
      const nextRows = Array.isArray(data) ? data : [];
      setRows(nextRows);
      setForm((current) => (
        isNewEmptyForm(current)
          ? { ...current, ...getInvoiceDefaults(nextRows) }
          : current
      ));
      return nextRows;
    } catch (error) {
      const message = getApiErrorMessage(error, 'No se pudieron cargar las sucursales.');
      console.error('Error cargando sucursales operativas:', error);
      setRows([]);
      setRowsError(message);
      return [];
    } finally {
      setLoadingRows(false);
    }
  };

  const loadProvincias = async () => {
    setLoadingProvincias(true);
    setProvinciasError('');
    try {
      const data = await listProvinciasOperativas();
      setProvincias(Array.isArray(data) ? data : []);
    } catch (error) {
      const message = getApiErrorMessage(error, 'No se pudieron cargar las provincias.');
      console.error('Error cargando provincias para sucursales:', error);
      setProvincias([]);
      setProvinciasError(message);
    } finally {
      setLoadingProvincias(false);
    }
  };

  const loadLocalidades = async (provinciaId, { preserveValue = false, nextValue = '' } = {}) => {
    if (!provinciaId) {
      setLocalidades([]);
      setLocalidadesError('');
      setForm((current) => ({ ...current, localidad_id: preserveValue ? current.localidad_id : '' }));
      return;
    }

    setLoadingLocalidades(true);
    setLocalidadesError('');

    try {
      const data = await listLocalidadesOperativas({ provincia_id: Number(provinciaId) });
      const nextLocalidades = Array.isArray(data) ? data : [];
      setLocalidades(nextLocalidades);

      const candidateValue = String(nextValue || '');
      const hasSelected = nextLocalidades.some((item) => String(item.id) === candidateValue);

      setForm((current) => ({
        ...current,
        localidad_id: preserveValue && hasSelected ? candidateValue : '',
      }));
    } catch (error) {
      const message = getApiErrorMessage(error, 'No se pudieron cargar las localidades.');
      console.error('Error cargando localidades para sucursales:', error);
      setLocalidades([]);
      setLocalidadesError(message);
      setForm((current) => ({ ...current, localidad_id: '' }));
    } finally {
      setLoadingLocalidades(false);
    }
  };

  useEffect(() => {
    loadRows();
    loadProvincias();
  }, []);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (formError) setFormError('');
  };

  const handleProvinciaChange = async (provinciaId) => {
    setForm((current) => ({
      ...current,
      provincia_id: provinciaId,
      localidad_id: '',
    }));
    if (formError) setFormError('');
    await loadLocalidades(provinciaId);
  };

  const handleEdit = async (row) => {
    const nextForm = {
      id: row.id,
      numero_sucursal: row.numero_sucursal || '',
      codigo: row.codigo || '',
      nombre: row.nombre || '',
      razon_social: row.razon_social || '',
      cuit: row.cuit || '',
      domicilio_fiscal: row.domicilio_fiscal || '',
      provincia_id: row.provincia_id ? String(row.provincia_id) : '',
      localidad_id: row.localidad_id ? String(row.localidad_id) : '',
      codigo_postal: row.codigo_postal || '',
      telefono: row.telefono || '',
      email: row.email || '',
      condicion_iva: row.condicion_iva || '',
      es_casa_central: Boolean(row.es_casa_central),
      es_hub_distribucion: Boolean(row.es_hub_distribucion),
      activa: Boolean(row.activa),
      observaciones: row.observaciones || '',
    };

    setForm(nextForm);
    setFormError('');

    if (nextForm.provincia_id) {
      await loadLocalidades(nextForm.provincia_id, {
        preserveValue: true,
        nextValue: nextForm.localidad_id,
      });
    } else {
      setLocalidades([]);
      setLocalidadesError('');
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = (sucursales = rows) => {
    setForm({ ...emptyForm, ...getInvoiceDefaults(sucursales) });
    setLocalidades([]);
    setLocalidadesError('');
    setFormError('');
  };

  const validateForm = () => {
    const missing = [];
    if (!String(form.numero_sucursal || '').trim()) missing.push('numero');
    if (!String(form.nombre || '').trim()) missing.push('nombre');
    if (!String(form.domicilio_fiscal || '').trim()) missing.push('domicilio fiscal');
    if (!String(form.provincia_id || '').trim()) missing.push('provincia');
    if (!String(form.localidad_id || '').trim()) missing.push('localidad');

    if (missing.length) {
      setFormError(`Complete los campos obligatorios: ${missing.join(', ')}.`);
      return false;
    }

    setFormError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        provincia_id: form.provincia_id ? Number(form.provincia_id) : null,
        localidad_id: form.localidad_id ? Number(form.localidad_id) : null,
      };

      if (form.id) {
        await updateSucursalOperativa(form.id, payload);
      } else {
        await createSucursalOperativa(payload);
      }

      const updatedRows = await loadRows();
      resetForm(updatedRows);
      await Swal.fire('Listo', 'Sucursal guardada correctamente.', 'success');
    } catch (error) {
      const message = getApiErrorMessage(error, 'No se pudo guardar la sucursal.');
      console.error('Error guardando sucursal operativa:', error);
      await Swal.fire('Error', message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row) => {
    try {
      await toggleSucursalOperativa(row.id, !row.activa);
      await loadRows();
    } catch (error) {
      const message = getApiErrorMessage(error, 'No se pudo actualizar el estado.');
      console.error('Error actualizando estado de sucursal:', error);
      await Swal.fire('Error', message, 'error');
    }
  };

  const localidadesDisabled = !form.provincia_id || loadingLocalidades || Boolean(localidadesError);
  const canEditGeo = !loadingProvincias && !provinciasError;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-2 px-2 py-2 md:px-3 md:py-3">
      <PageHeader
        title="Sucursales operativas"
        subtitle=""
        actions={<a href="/administracion/empresa/localidades-atendidas" className="inline-flex h-9 items-center rounded-xl border border-[rgb(var(--border))] px-3 text-sm font-medium text-[rgb(var(--fg))]">Localidades atendidas</a>}
      />

      {rowsError ? (
        <SectionAlert>
          <p className="font-semibold">No se pudo cargar el listado de sucursales.</p>
          <p className="mt-0.5 text-[rgba(var(--fg),0.9)]">{rowsError}</p>
        </SectionAlert>
      ) : null}

      {provinciasError ? (
        <SectionAlert tone="warning">
          <p className="font-semibold">No se pudieron cargar provincias y localidades.</p>
          <p className="mt-0.5 text-[rgba(var(--fg),0.9)]">{provinciasError}</p>
        </SectionAlert>
      ) : null}

      <section className={`${panelClassName} grid grid-cols-12 gap-2`}>
        <div className="col-span-12 rounded-xl border border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.06)] px-3 py-2">
          <p className="text-sm font-semibold text-[rgb(var(--fg))]">Datos del emisor para comprobantes</p>
          <p className="text-xs text-[rgba(var(--muted),0.95)]">Se imprimen en las facturas emitidas desde esta sucursal. Si un dato queda vacío, no se muestra.</p>
          {!form.id && rows.length ? (
            <p className="mt-1 text-xs text-[rgba(var(--muted),0.95)]">Se precargan desde la casa central para agilizar el alta; podés modificarlos si esta sucursal factura distinto.</p>
          ) : null}
        </div>
        <div className="col-span-12 md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Numero</label>
          <input className={inputClassName} value={form.numero_sucursal} onChange={(e) => handleChange('numero_sucursal', e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Nombre</label>
          <input className={inputClassName} value={form.nombre} onChange={(e) => handleChange('nombre', e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-7">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Razón social para comprobantes</label>
          <input className={inputClassName} value={form.razon_social} onChange={(e) => handleChange('razon_social', e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">CUIT para comprobantes</label>
          <input className={inputClassName} value={form.cuit} onChange={(e) => handleChange('cuit', e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-7">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Domicilio fiscal</label>
          <input className={inputClassName} value={form.domicilio_fiscal} onChange={(e) => handleChange('domicilio_fiscal', e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Provincia</label>
          <select
            className={inputClassName}
            value={form.provincia_id}
            onChange={(e) => handleProvinciaChange(e.target.value)}
            disabled={!canEditGeo}
          >
            <option value="">
              {loadingProvincias ? 'Cargando provincias...' : provinciasError ? 'Provincias no disponibles' : 'Seleccionar provincia'}
            </option>
            {provincias.map((provincia) => (
              <option key={provincia.id} value={provincia.id}>{provincia.nombre}</option>
            ))}
          </select>
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Localidad</label>
          <select
            className={inputClassName}
            value={form.localidad_id}
            onChange={(e) => handleChange('localidad_id', e.target.value)}
            disabled={localidadesDisabled}
          >
            <option value="">
              {!form.provincia_id
                ? 'Seleccionar provincia primero'
                : loadingLocalidades
                  ? 'Cargando localidades...'
                  : localidadesError
                    ? 'Localidades no disponibles'
                    : 'Seleccionar localidad'}
            </option>
            {localidades.map((localidad) => (
              <option key={localidad.id} value={localidad.id}>{localidad.nombre}</option>
            ))}
          </select>
          {localidadesError ? (
            <p className="mt-1 text-xs text-[rgb(var(--danger,220_38_38))]">{localidadesError}</p>
          ) : null}
        </div>
        <div className="col-span-6 md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">C.P.</label>
          <input className={inputClassName} value={form.codigo_postal} onChange={(e) => handleChange('codigo_postal', e.target.value)} />
        </div>
        <div className="col-span-6 md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Telefono</label>
          <input className={inputClassName} value={form.telefono} onChange={(e) => handleChange('telefono', e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Email</label>
          <input className={inputClassName} value={form.email} onChange={(e) => handleChange('email', e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Condición IVA para comprobantes</label>
          <select className={inputClassName} value={form.condicion_iva} onChange={(e) => handleChange('condicion_iva', e.target.value)}>
            <option value="">Sin informar</option>
            <option value="RI">Responsable inscripto</option>
            <option value="MT">Monotributista</option>
            <option value="EX">Exento</option>
            <option value="CF">Consumidor final</option>
            <option value="RNI">Responsable no inscripto</option>
          </select>
        </div>
        <div className="col-span-12 md:col-span-9">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Observaciones</label>
          <textarea className={`${inputClassName} min-h-[62px] py-2`} value={form.observaciones} onChange={(e) => handleChange('observaciones', e.target.value)} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <div className="grid grid-cols-1 gap-2 rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.34)] p-3">
            {[
              ['es_casa_central', 'Es casa central'],
              ['es_hub_distribucion', 'Es hub / distribuidora'],
              ['activa', 'Sucursal activa'],
            ].map(([field, label]) => (
              <label key={field} className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
                <input type="checkbox" checked={Boolean(form[field])} onChange={(e) => handleChange(field, e.target.checked)} />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
        {formError ? (
          <div className="col-span-12">
            <SectionAlert tone="warning">{formError}</SectionAlert>
          </div>
        ) : null}
        <div className="col-span-12">
          <StickyActions
            onCancel={resetForm}
            onSubmit={handleSubmit}
            isSubmitting={saving}
            primaryLabel={form.id ? 'Guardar cambios' : 'Crear sucursal'}
            secondaryLabel="Limpiar"
          />
        </div>
      </section>

      <section className={panelClassName}>
        <div className="mb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Listado</h2>
          <input className={`${inputClassName} md:w-[320px]`} placeholder="Buscar por numero, nombre o plaza" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        {loadingRows ? (
          <div className="py-8 text-center text-sm text-[rgba(var(--muted),0.88)]">Cargando sucursales...</div>
        ) : filteredRows.length === 0 ? (
          <EmptyState
            title={rowsError ? 'Sin datos para mostrar' : 'Sin sucursales'}
            description={rowsError ? 'Revisa la conexion con el backend o los permisos del usuario.' : 'Crea la primera sucursal operativa para comenzar.'}
          />
        ) : (
          <div className="max-h-[44vh] overflow-auto rounded-2xl border border-[rgba(var(--border),0.55)]">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[rgba(var(--subpanel),0.58)] text-[rgba(var(--muted),0.95)]">
                <tr>
                  <th className="px-3 py-2">Numero</th>
                  <th className="px-3 py-2">Nombre</th>
                  <th className="px-3 py-2">Provincia / localidad</th>
                  <th className="px-3 py-2">Hub</th>
                  <th className="px-3 py-2">Activa</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id} className="border-t border-[rgba(var(--border),0.42)]">
                    <td className="px-3 py-2 font-medium">{row.numero_sucursal || row.codigo || row.id}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.nombre}</div>
                    </td>
                    <td className="px-3 py-2">{[row.provincia_nombre, row.localidad_nombre].filter(Boolean).join(' / ') || 'Sin parametrizar'}</td>
                    <td className="px-3 py-2"><BooleanPill value={Boolean(row.es_hub_distribucion)} /></td>
                    <td className="px-3 py-2"><BooleanPill value={Boolean(row.activa)} /></td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => handleEdit(row)}>Editar</button>
                        <button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => handleToggle(row)}>{row.activa ? 'Desactivar' : 'Activar'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
